import React, {FC} from 'react';
import cn from 'classnames';

import {useTimeoutAction} from '@/hooks/useTimeoutAction';

import {AUTO_ADVANCE_MS} from '../../constants';
import {useFhs, useFhsActor} from '../../store/FhsContext/hooks';
import type {FhsField, IconOption} from '../../types';
import {buildIconClassName} from '../../utils';

import styles from './styles.module.scss';

interface QuestionCardRadioProps {
  field: FhsField;
  options: IconOption[];
  /**
   * Predicate run against the just-selected value. If it returns true, the
   * form auto-advances to the next step after a short delay. Defaults to
   * always advance — override when a value should keep the user on the
   * current step (e.g. needing to fill a follow-up field).
   */
  shouldAdvanceFor?: (value: string) => boolean;
}

const alwaysAdvance = () => true;

const QuestionCardRadio: FC<QuestionCardRadioProps> = ({
  field,
  options,
  shouldAdvanceFor = alwaysAdvance,
}) => {
  const actor = useFhsActor();
  const {formData, errors, touched} = useFhs();
  const schedule = useTimeoutAction();

  const current = formData[field];
  const error = touched[field] ? errors[field] : undefined;

  const handleSelect = (value: string) => {
    actor.send({type: 'UPDATE_FIELD', field, value});

    if (shouldAdvanceFor(value)) {
      schedule(() => actor.send({type: 'NEXT'}), AUTO_ADVANCE_MS);
    }
  };

  return (
    <div className={styles.questionCardRadio}>
      <div className={styles.questionCardRadio__grid}>
        {options.map((opt) => (
          <label key={opt.value} className={styles.questionCardRadio__card}>
            <input
              type="radio"
              name={String(field)}
              value={opt.value}
              checked={current === opt.value}
              aria-label={opt.label}
              className={styles.questionCardRadio__input}
              onChange={() => handleSelect(opt.value)}
              onClick={() => {
                if (current === opt.value) handleSelect(opt.value);
              }}
            />
            <i
              className={cn(
                styles.questionCardRadio__icon,
                buildIconClassName(opt, current === opt.value),
              )}
              aria-hidden
            />
            <span className={styles.questionCardRadio__label}>{opt.label}</span>
          </label>
        ))}
      </div>
      <div className={styles.questionCardRadio__errorSlot}>
        {error && <p className={styles.questionCardRadio__error}>{error}</p>}
      </div>
    </div>
  );
};

export default QuestionCardRadio;
